import { AfterViewInit, Component, inject, OnInit, output, viewChild } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { INotificationComponent } from '../../../../../../types/componentInterfaces/inotificationcomponent';
import { CommunityService, ModalService, NotificationRecordableService, OrgSelectionService, RecordablesService, SubGroupsService } from './../../../../../../services';
import { MessageService } from './../../../../../../services/message.service';
import { Community, Contact, Market, NotificationRecordable, RecordableCategory, SubGroup } from './../../../../../../types';
import { NotificationRecordableViewModel } from './notificationrecordableviewmodel';
import { concatMap, EMPTY, forkJoin, of, switchMap, takeUntil, tap } from 'rxjs';
import { HasValuePipe } from '../../../../../../pipes/hasvalue.pipe';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { SharedModule } from 'primeng/api';
import { ListboxModule } from 'primeng/listbox';
import { SelectModule } from 'primeng/select';

import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { UnsubscribeOnDestroy } from '../../../../../../classes';


@Component({
	selector: 'recordable-notifications',
	templateUrl: './recordable.notifications.component.html',
	styleUrls: ['./recordable.notifications.component.scss'],
	standalone: true,
  imports: [FormsModule, ToggleSwitchModule, SelectModule, ListboxModule, SharedModule, CheckboxModule, MultiSelectModule, HasValuePipe]
})
export class RecordableNotificationsComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit, INotificationComponent {
	allCommChbx: boolean = false;
	allCatChbx: boolean = false;
	allOwnerChbx: boolean = false;
	ownerOptions: Contact[] = [];
	communityOptions: Community[] = [];
	categoryOptions: RecordableCategory[] = [];
	subGroupOptions: SubGroup[] = [];
	subGroupCommunityOptions: any;
	market: Market;
	isSwitchOn: boolean = false;
	notificationRecordable: NotificationRecordable = new NotificationRecordable();
	notificationRecordableViewModel: NotificationRecordableViewModel = new NotificationRecordableViewModel();
	errorMessage: string;

	readonly complete = output<boolean>();

	readonly recordableNotificationForm = viewChild<NgForm>('recordableNotificationForm');

	private communityService = inject(CommunityService);
	private messageService = inject(MessageService);
	private modalService = inject(ModalService);
	private notificationRecordableService = inject(NotificationRecordableService);
	private orgSelectionService = inject(OrgSelectionService);
	private recordablesService = inject(RecordablesService);
	private subGroupsService = inject(SubGroupsService);

	constructor() { super(); }

	ngAfterViewInit(): void {
		this.recordableNotificationForm().form.valueChanges
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe((value) => {
				this.messageService.sendMessage({ key: 'hasChanges', value: this.recordableNotificationForm().dirty });
			});
	}

	load(id: number) {
		this.errorMessage = "";
		this.getSelectedMarket(id);
	}

	isNotificationFormDirty(): boolean {
		return !!this.recordableNotificationForm()?.form?.dirty;
	}

	ngOnInit(): void {
	}

	getSelectedMarket(id) {
		this.categoryOptions = [];
		this.communityOptions = [];
		this.subGroupOptions = [];
		this.ownerOptions = [];
		this.orgSelectionService.selectedMarket
			.pipe(
				takeUntil(this.ngUnsubscribe$),
				concatMap((market) => {
					this.market = market;
					return forkJoin({
						categoryOptions: this.recordablesService.getRecordableCategories(),
						communityOptions: this.communityService.getConstructionActiveCommunities(this.market.orgId),
						subGroupOptions: this.subGroupsService.getSubGroups([this.market.orgId]),
						ownerOptions: this.recordablesService.getRecordableContacts(this.market.sourceKey)
					})
				}),
				switchMap(response => {
					this.categoryOptions = response.categoryOptions;
					this.communityOptions = response.communityOptions;
					this.subGroupOptions = response.subGroupOptions;
					this.ownerOptions = response.ownerOptions;
					return this.getNotificationRecordable$(id);
				})
			)
			.subscribe();
	}

	getNotificationRecordable$(notificationId: number) {
		if (notificationId > 0) { // edit
			return this.notificationRecordableService.getNotificationRecordable(notificationId)
				.pipe(
					concatMap((notificationRecordable) => {
						this.notificationRecordableViewModel = new NotificationRecordableViewModel();
						this.notificationRecordableViewModel.setProperties(notificationRecordable, this.categoryOptions, this.ownerOptions,
							this.communityOptions, this.subGroupOptions);
						this.notificationRecordable = notificationRecordable;
						this.notificationRecordableViewModel.selectedSubGroup ? this.isSwitchOn = true : this.isSwitchOn = false;
						this.setCheckboxes();
						return this.onHandleSubGroupChange$();
					})
				)

		}
		else { // add
			this.recordableNotificationForm().form.reset();
			this.notificationRecordableViewModel = new NotificationRecordableViewModel();
			this.notificationRecordable = new NotificationRecordable();
			return of(undefined);
		}
	}

	onHandleSubGroupChange$() {
		if (this.notificationRecordableViewModel.selectedSubGroup) {
			return this.subGroupsService.getSubGroup(this.notificationRecordableViewModel.selectedSubGroup.subGroupID)
				.pipe(
					tap((subGroup) => {
						this.subGroupCommunityOptions = subGroup.communities;
					})
				);
		}
		else
			return of(undefined);
	}

	onHandleSubGroupChange() {
		this.onHandleSubGroupChange$()
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe();
	}

	onHandleCommChbxChange(e) {
		if (e.checked) {
			this.notificationRecordableViewModel.selectedCommunities = [];
		}
	}

	onHandleCatChbxChange(e) {
		if (e.checked) {
			this.notificationRecordableViewModel.selectedCategories = [];
		}
	}

	onHandleOwnerChbxChange(e) {
		if (e.checked) {
			this.notificationRecordableViewModel.selectedOwners = [];
		}
	}

	onHandleCommChange(e) {
		this.allCommChbx = false;
	}

	onHandleCatChange(e) {
		this.allCatChbx = false;
	}

	onHandleOwnerChange(e) {
		this.allOwnerChbx = false;
	}

	setCheckboxes() {
		this.notificationRecordableViewModel?.notificationCommunities.length === 0 && this.notificationRecordableViewModel.selectedSubGroup === null ? this.allCommChbx = true : this.allCommChbx = false;
		this.notificationRecordableViewModel?.notificationRecordableCategories.length === 0 ? this.allCatChbx = true : this.allCatChbx = false;
		this.notificationRecordableViewModel?.notificationRecordableOwners.length === 0 ? this.allOwnerChbx = true : this.allOwnerChbx = false;
	}

	onClose() {
		this.complete.emit(false);
		this.recordableNotificationForm().form.reset();
	}

	onCancel() {
		this.modalService.showWarningModal("Are you sure you want to discard changes?")
			.pipe(
				takeUntil(this.ngUnsubscribe$),
				switchMap((isYesClicked: boolean) => {
					if (!isYesClicked) return EMPTY;

					this.recordableNotificationForm().form.markAsPristine();

					if (this.notificationRecordableViewModel.notificationId > 0) {
						this.notificationRecordableViewModel.setProperties(this.notificationRecordable, this.categoryOptions, this.ownerOptions, this.communityOptions, this.subGroupOptions);
						this.setCheckboxes();
						return this.onHandleSubGroupChange$();
					} else {
						this.recordableNotificationForm().form.reset();
						this.notificationRecordableViewModel = new NotificationRecordableViewModel();
						this.notificationRecordable = new NotificationRecordable();
						return of(null);
					}

				}))
			.subscribe(() => {
				if (!this.notificationRecordableViewModel.selectedSubGroup) {
					this.isSwitchOn = false; // default to Communities;         
				} else {
					this.isSwitchOn = true; // default to Subgroups
				}
			});
	}

	onSave(): void {
		this.errorMessage = "";
		this.notificationRecordableViewModel.mapEdhPropertiesToPcpProperties(this.market.orgId);
		this.notificationRecordable = this.notificationRecordableViewModel.getNotificationRecordable();

		if ((!this.allCommChbx && this.notificationRecordableViewModel.selectedCommunities.length === 0 &&
			!this.notificationRecordableViewModel.selectedSubGroup) ||
			(!this.allCatChbx && this.notificationRecordableViewModel.selectedCategories.length === 0) ||
			(!this.allOwnerChbx && this.notificationRecordableViewModel.selectedOwners.length === 0)) {
			this.errorMessage = "Please select at least one item in each section.";
		} else {
			this.notificationRecordableService.saveNotificationRecordable(this.notificationRecordable)
				.pipe(takeUntil(this.ngUnsubscribe$))
				.subscribe((response) => {
					this.complete.emit(true);
					this.recordableNotificationForm().form.reset();
				});
		}
	}
}
